import * as React from 'react';

import './index.scss';

import Toolbar from './Toolbar';
import PropertyBar from './PropertyBar';
import FontLoadingIndicator from './FontLoadingIndicator';
import { App } from '../../../pkg';
import { registerResize } from '../../utils/dom-events';
import { makeLooper } from '../../utils/loop';
import Fps from './Fps';
import Textarea from './Textarea';
import { CursorDrawMode } from '../../models/cursor';
import { registerEventReceiver, onTextEdit } from '../../wasmEventReceiver';
import { selectImage } from '../../utils/file';
import { imageStorage } from '../../service/image_storage';
import { getCanvasSizeInfo } from '../../utils/dom-utils';
import { mapKey } from '../../utils/key';
import { FontManager } from '../../utils/fontManager';

interface IApplicationProps {
  app: App;
  fontManager: FontManager;
}

enum MouseEventType {
  Down,
  Up,
  Move,
  DblClick,
}

const resetAppSize = (app: App): void => {
  const sizeInfo = getCanvasSizeInfo();
  app.handle_resize(sizeInfo.width, sizeInfo.height, sizeInfo.dpr);
};

const Application: React.FC<IApplicationProps> = ({ app, fontManager }: IApplicationProps) => {
  const looper = makeLooper(app);
  const [currentMode, setCurrentMode] = React.useState<CursorDrawMode>(CursorDrawMode.Cursor);
  const [version, setVersion] = React.useState(0);
  const [editText, setEditText] = React.useState<null | {
    left: number;
    top: number;
    text: string;
    elementId?: string; // 可选的元素 ID，用于区分创建新文字还是编辑已有文字
    fontSize?: number;
    color?: string;
    fontFamily?: string;
  }>(null);

  React.useEffect(() => {
    resetAppSize(app);
    looper.start();
    registerEventReceiver(() => {
      setTimeout(() => {
        const mode = app.get_cursor_draw_mode();
        setCurrentMode(mode);
      }, 0);
    });

    // 监听文字编辑事件
    const unsubscribeTextEdit = onTextEdit((elementId: string) => {
      console.log('Edit text element:', elementId);
      const textInfo = app.get_text_element_info(elementId);
      console.log('textInfo from Rust:', textInfo);
      
      if (textInfo) {
        // Handle both Map (wasm-bindgen default for some structs) and Object
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const getValue = (key: string) => (textInfo instanceof Map ? textInfo.get(key) : (textInfo as any)[key]);

        const x = Number(getValue('x'));
        const y = Number(getValue('y'));
        
        if (!isNaN(x) && !isNaN(y)) {
          const id = getValue('id');
          app.set_editing_id(id); // 通知 Rust 隐藏该元素
          setEditText({
            left: x + 40,
            top: y + 40,
            text: getValue('text'),
            elementId: id,
            fontSize: getValue('fontSize'),
            color: getValue('fillColor'),
            fontFamily: getValue('fontFamily'),
          });
        } else {
          console.error('Invalid coordinates in textInfo:', textInfo);
        }
      }
    });

    // 监听字体加载状态变化，触发重新渲染
    const unsubscribe = fontManager.onFontStateChange(() => {
      setVersion(Math.random());
    });

    const handleResize = () => {
      resetAppSize(app);
    };
    const customResizeEventName = registerResize();
    window.addEventListener(customResizeEventName, handleResize);

    return () => {
      window.removeEventListener(customResizeEventName, handleResize);
      unsubscribe();
      unsubscribeTextEdit();
    };
  }, [app, fontManager]);

  React.useEffect(() => {
    const finishTextEditing = () => {
      if (editText && editText.text.trim()) {
        if (editText.elementId) {
          // 编辑已有文字元素
          app.update_text_node(editText.elementId, editText.text.trim());
        } else {
          // 创建新文字元素
          app.create_text_node(editText.text.trim(), editText.left - 40, editText.top - 40);
        }
      }
      app.set_editing_id(undefined); // 清空编辑 ID，恢复显示
      setEditText(null);
      app.change_cursor_draw_mode(CursorDrawMode.Cursor);
    };

    const handleMouseEvent = (eventType: MouseEventType, updateVersion: boolean, event: MouseEvent) => {
      if (event.target instanceof Element && event.target.tagName === 'TEXTAREA') {
        return;
      }
      const x = event.clientX - 40;
      const y = event.clientY - 40;
      if (x < 0 || y < 0) {
        return;
      }
      if (currentMode === CursorDrawMode.Text) {
        if (eventType === MouseEventType.Down) {
          if (!editText) {
            setEditText({
              left: event.clientX,
              top: event.clientY,
              text: '',
            });
          } else {
            finishTextEditing();
          }
        }
        return;
      }
      app.handle_mouse(eventType, x, y, {
        shift: event.shiftKey,
        alt: event.altKey,
        ctrl: event.metaKey,
      });
      if (updateVersion) {
        setVersion(Math.random());
      }
    };

    const handleMouseDown = handleMouseEvent.bind(null, MouseEventType.Down, false);
    document.addEventListener('mousedown', handleMouseDown);

    const handleMouseMove = handleMouseEvent.bind(null, MouseEventType.Move, false);
    document.addEventListener('mousemove', handleMouseMove);

    const handleMouseUp = handleMouseEvent.bind(null, MouseEventType.Up, true);
    document.addEventListener('mouseup', handleMouseUp);

    document.addEventListener('dblclick', handleMouseEvent.bind(null, MouseEventType.DblClick, true));

    const handleKeyDown = (e: KeyboardEvent) => {
      // eslint-disable-next-line
      // @ts-ignore
      const tagName = e.target?.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
        return;
      }
      if (currentMode === CursorDrawMode.Text) {
        if (e.key === 'Escape') {
          finishTextEditing();
        }
        return;
      }
      const event_handled = app.handle_key_down({
        key:  mapKey(e.key),
        shift: e.shiftKey,
        alt: e.altKey,
        ctrl: e.metaKey,
      });
      if (event_handled) {
        e.stopPropagation();
        e.preventDefault();
      }
      console.log(event_handled);
      // let captured = true;
      // if (e.metaKey) {
        // switch (e.key) {
          // case 'a':
            // app.select_all();
            // break;
          // case 'g':
            // app.group_selection();
            // break;
          // case 's':
            // app.save();
            // break;
          // case '0':
          // case '1':
          // case '2':
          // case '3':
          // case '4':
          // case '5':
            // app.set_viewport(e.key);
            // break;
          // case 'z':
            // if (e.shiftKey) {
              // app.handle_redo();
            // } else {
              // app.handle_undo();
            // }
            // break;
          // case 'c':
            // app.handle_copy();
            // break;
          // case 'x':
            // app.handle_cut();
            // break;
          // case 'v':
            // app.handle_paste();
            // break;
          // case 'd':
            // app.handle_clone();
            // break;
          // default:
            // captured = false;
            // break;
        // }
      // } else {
        // switch (e.key) {
          // case 'Backspace':
            // app.handle_delete();
            // break;
          // case 'r':
            // app.change_cursor_draw_mode(CursorDrawMode.Rectangle);
            // break;
          // case 'o':
            // app.change_cursor_draw_mode(CursorDrawMode.Oval);
            // break;
          // case 't':
            // app.change_cursor_draw_mode(CursorDrawMode.Text);
            // break;
          // case 'Escape':
            // app.handle_escape_key();
            // break;
          // default:
            // captured = false;
            // break;
        // }
      // }
    };
    document.addEventListener('keydown', handleKeyDown);

    const handleKeyUp = (e: KeyboardEvent) => {
      // eslint-disable-next-line
      // @ts-ignore
      const tagName = e.target?.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
        return;
      }
      if (currentMode === CursorDrawMode.Text) {
        if (e.key === 'Escape') {
          finishTextEditing();
        }
        return;
      }
      app.handle_key_up({
        key:  mapKey(e.key),
        shift: e.shiftKey,
        alt: e.altKey,
        ctrl: e.metaKey,
      });
      e.stopPropagation();
      e.preventDefault();
    };
    document.addEventListener('keyup', handleKeyUp);

    const handleWheel = (event: WheelEvent) => {
      if (currentMode === CursorDrawMode.Text) {
        return;
      }
      // 阻止浏览器默认行为（包括 Mac 上的后退手势和页面缩放）
      event.preventDefault();

      // Mac 触控板双指缩放通常会触发带有 ctrlKey 的 wheel 事件
      if (event.ctrlKey || event.metaKey) {
        const x = event.pageX - 40;
        const y = event.pageY - 40;
        // deltaY 在缩放手势中通常较小，除以一个系数来控制灵敏度
        // 注意：方向可能需要反转，尝试 -event.deltaY
        app.handle_zoom(-event.deltaY / 100, x, y);
      } else {
        app.handle_wheel(-event.deltaX, -event.deltaY);
      }
    };
    document.addEventListener('wheel', handleWheel, {
      passive: false,
    });

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      document.removeEventListener('keydown', handleKeyDown);

      document.removeEventListener('wheel', handleWheel);
    };
  }, [currentMode, editText, app]);

  return (
    <div className="application">
      <div className="left">
        <Toolbar
          app={app}
          currentMode={currentMode}
          onChangeMode={mode => {
            if (mode === CursorDrawMode.Image) {
              selectImage().then(async imageFile => {
                const contentId = await imageStorage.saveImage(imageFile);
                const record = await imageStorage.getImage(contentId);
                if (record) {
                  app.create_image(contentId, record.width, record.height);
                  setVersion(v => v + 1);
                }
              });
            } else {
              app.change_cursor_draw_mode(mode);
            }
          }}
        />
      </div>
      <div className="right">
        <PropertyBar app={app} version={version} />
      </div>
      <Fps fps={looper.fps} />
      <FontLoadingIndicator fontManager={fontManager} />
      {editText && (
        <Textarea
          text={editText.text}
          left={editText.left}
          top={editText.top}
          fontSize={editText.fontSize}
          color={editText.color}
          fontFamily={editText.fontFamily}
          onChangeText={text => {
            setEditText(Object.assign({}, editText, { text }));
          }}
          onSubmit={() => {
             // Logic extracted from finishTextEditing effect or similar
             if (editText && editText.text.trim()) {
                if (editText.elementId) {
                  app.update_text_node(editText.elementId, editText.text.trim());
                } else {
                  app.create_text_node(editText.text.trim(), editText.left - 40, editText.top - 40);
                }
             }
             app.set_editing_id(undefined);
             setEditText(null);
             app.change_cursor_draw_mode(CursorDrawMode.Cursor);
          }}
          onBlur={() => {
             // Same logic as onSubmit for now
             if (editText && editText.text.trim()) {
                if (editText.elementId) {
                  app.update_text_node(editText.elementId, editText.text.trim());
                } else {
                  app.create_text_node(editText.text.trim(), editText.left - 40, editText.top - 40);
                }
             }
             app.set_editing_id(undefined);
             setEditText(null);
             app.change_cursor_draw_mode(CursorDrawMode.Cursor);
          }}
        />
      )}
    </div>
  );
};

export default Application;
